/**
 * El plano de AWAP 2: dónde está el libro, en los dos ejes que no se suman.
 *
 * POR QUÉ UN DIBUJO Y NO TRES CIFRAS. Las tres tarjetas de arriba dicen cobertura, puntuación y
 * cuadrante, y aun así no dicen lo que importa: que son medidas de cosas distintas. Un plano lo
 * dice sin explicarlo — no hay forma de mirarlo y creer que uno es la mitad del otro.
 *
 * Tres reglas de la especificación, dibujadas:
 *   · el eje X lleva sus SEIS anclas marcadas, porque la cobertura es discreta y no un porcentaje;
 *   · la frontera en 50 va discontinua: es convencional, y un 51 y un 49 no son cosas distintas (§5);
 *   · el punto va CON su banda, nunca la cifra sola.
 *
 * Y las cuatro celdas pesan lo mismo en el dibujo: Q4 no es el cuadrante del fracaso (§1.5). Solo
 * se resalta la celda donde está el libro, que es información, no un juicio.
 */
import { puntoDelPlano } from "../logica";
import type { Posicion } from "../api";

// Las seis anclas. La de 85 (biblia) va sin nombre: 75 y 85 quedan tan juntas que las dos palabras
// se tocaban. El número sí va, que es lo que importa para ver que la escala es discreta.
const ANCLAS: { v: number; et?: string }[] = [
  { v: 0, et: "fichero" },
  { v: 20, et: "manuscrito" },
  { v: 50, et: "borrador" },
  { v: 75, et: "escaleta" },
  { v: 85 },
  { v: 100, et: "premisa" },
];

const CELDAS: { id: "Q1" | "Q2" | "Q3" | "Q4"; cx: number; cy: number }[] = [
  { id: "Q2", cx: 25, cy: 75 }, { id: "Q1", cx: 75, cy: 75 },
  { id: "Q3", cx: 25, cy: 25 }, { id: "Q4", cx: 75, cy: 25 },
];

// El lienzo, en unidades del viewBox. Caja de datos de 0 a 100 en los dos ejes.
//
// 17-sep (Rais): el SVG ya no se estira a todo lo ancho de la tarjeta —un solo punto en un lienzo
// de pantalla completa se veía tosco y los números de los ejes salían enormes—. Va en una columna
// con tope (`.plano svg` en estilo.css) y las cifras al lado. El título del eje Y va en horizontal,
// arriba: rotado costaba leerlo. 4:3 (480×360), el mismo aspecto que su marco `.grafico`.
// ARR deja sitio para él; IZQ ya no tiene que alojar texto girado.
const W = 480, H = 360, IZQ = 26, DER = 12, ARR = 30, ABA = 46;
const ex = (v: number) => IZQ + (W - IZQ - DER) * v / 100;
const ey = (v: number) => (H - ABA) - (H - ABA - ARR) * v / 100;
const HUECO = 1.5; // separación entre celdas: las divide el fondo, no un borde

export default function Plano({ pos }: { pos: Posicion }) {
  const p = puntoDelPlano(pos.cobertura.valor, pos.has?.valor ?? null, pos.has?.banda);
  const sigla = pos.eje_y?.sigla ?? "HAS";
  const donde = pos.cuadrante.id;
  const dentro = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
  const px = dentro(ex(p.x), ex(0) + 10, ex(100) - 10);
  const py = p.y == null ? 0 : dentro(ey(p.y), ey(100) + 5, ey(0) - 5);
  const rotulo = p.y == null
    ? `Cobertura ${p.x} y sin puntuación: sin proceso registrado, el libro está en Q3, sin acreditar.`
    : `Cobertura ${p.x} en el eje X y ${sigla} ${p.y}${p.banda ? `, banda ${p.banda.desde}-${p.banda.hasta}` : ""} en el eje Y: cuadrante ${donde}, ${pos.cuadrante.nombre}.`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={rotulo}>
      {/* Las cuatro celdas, con el mismo fondo y separadas por un hueco, sin bordes. La del libro,
          en oro suave; las otras tres, iguales entre sí: Q4 no es el cuadrante del fracaso (§1.5).
          Sin puntuación no se resalta NINGUNA: pintar abajo a la izquierda con cobertura 100 diría
          que el registro empezó tarde, justo lo contrario de lo que pasa. */}
      {CELDAS.map((c) => (
        <rect key={c.id}
              x={ex(c.cx === 25 ? 0 : 50) + HUECO} y={ey(c.cy === 75 ? 100 : 50) + HUECO}
              width={ex(50) - ex(0) - 2 * HUECO} height={ey(0) - ey(50) - 2 * HUECO} rx="6"
              fill={p.y != null && c.id === donde ? "var(--acc-suave)" : "var(--sup3)"}
              opacity={p.y != null && c.id === donde ? 1 : 0.6} />
      ))}
      {CELDAS.map((c) => (
        <text key={c.id} x={ex(c.cx === 25 ? 0 : 50) + 10} y={ey(c.cy === 75 ? 100 : 50) + 16}
              className="eje celda" fill={p.y != null && c.id === donde ? "var(--acc)" : "var(--mut2)"}>
          {c.id}
        </text>
      ))}

      {/* La frontera convencional, discontinua y tenue: es una convención, no un muro (§5) */}
      <line x1={ex(50)} y1={ey(0)} x2={ex(50)} y2={ey(100)} stroke="var(--linea)" strokeWidth="0.8" strokeDasharray="2 4" />
      <line x1={ex(0)} y1={ey(50)} x2={ex(100)} y2={ey(50)} stroke="var(--linea)" strokeWidth="0.8" strokeDasharray="2 4" />

      {/* El eje X con sus anclas: la cobertura es discreta. Números pequeños y tenues; el dato es el punto. */}
      {ANCLAS.map((a) => (
        <g key={a.v}>
          <line x1={ex(a.v)} y1={ey(0) + 2} x2={ex(a.v)} y2={ey(0) + 5} stroke="var(--linea)" strokeWidth="0.8" />
          <text x={ex(a.v)} y={ey(0) + 14} textAnchor="middle" className="eje n">{a.v}</text>
          {a.et && <text x={ex(a.v)} y={ey(0) + 24} textAnchor="middle" className="eje">{a.et}</text>}
        </g>
      ))}
      {[0, 50, 100].map((v) => (
        <text key={v} x={ex(0) - 6} y={ey(v) + 3} textAnchor="end" className="eje n">{v}</text>
      ))}
      <text x={W - DER} y={H - 4} textAnchor="end" className="eje titulo">cobertura · desde cuándo hay registro →</text>
      <text x={ex(0)} y={ARR - 12} textAnchor="start" className="eje titulo">↑ {sigla} · lo observado</text>

      {/* El libro: su banda y su punto. Sin puntuación, ni punto ni banda: solo se dice.
          `px`/`py` se meten dentro de la caja: en cobertura 0 o 100 —fichero o premisa, los dos
          extremos reales— el punto caía justo encima del eje y se leía como un adorno del borde. */}
      {p.y != null && p.banda && (
        <rect x={px - 8} y={ey(p.banda.hasta)} width="16" height={Math.max(2, ey(p.banda.desde) - ey(p.banda.hasta))}
              rx="3" fill="var(--acc)" opacity="0.18" />
      )}
      {p.y != null && <circle cx={px} cy={py} r="4.5" fill="var(--acc)" stroke="var(--sup)" strokeWidth="2" />}
      {/* La cifra exacta con su banda, al lado del punto: la norma obliga a acompañarla. Al otro
          lado si el punto está en la mitad derecha. */}
      {p.y != null && (
        <text x={p.x < 50 ? px + 11 : px - 11} y={py + 3} textAnchor={p.x < 50 ? "start" : "end"}
              className="eje punto-et" fill="var(--acc)">
          {p.y}{p.banda ? ` · banda ${p.banda.desde}-${p.banda.hasta}` : ""}
        </text>
      )}
      {/* Sin puntuación: su cobertura sí se sabe, así que se marca su columna y se dice que del eje
          Y no hay nada que decir todavía. Dentro de la caja, para no pisar el título del eje Y. */}
      {p.y == null && (
        <>
          <line x1={px} y1={ey(0)} x2={px} y2={ey(100)} stroke="var(--acc)" strokeWidth="1" strokeDasharray="2 3" />
          <text x={p.x > 50 ? px - 6 : px + 6} y={ey(90)} textAnchor={p.x > 50 ? "end" : "start"} className="eje punto-et" fill="var(--acc)">
            cobertura {p.x} · sin puntuación todavía
          </text>
        </>
      )}
    </svg>
  );
}
